import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/hooks';
import { selectProject } from '@store/selectors/monitor.selectors';
import { tradeLabel } from '@utils/tradeLabel';
import { professionChipStyle } from '@utils/professionColor';
import { NO_FLOOR_LABEL, type SafetyRow } from '@store/selectors/monitor.selectors';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { AlertCircleFilledIcon } from '@utils/icons';
import styles from './leftRail.module.css';

interface ISafetyAlertRowProps {
  row: SafetyRow;
  /** Opens the alert's detail dialog, which the list renders. */
  onOpen: (eventId: string) => void;
}

/**
 * One row in the Safety Log. Mirrors trusstor's `SafetyAlertRow`:
 * red/orange exclamation icon · violation-type label · HH:MM ·
 * floor nick · trade/profession/manager-coloured pill.
 *
 * Times are formatted in the project's configured tz (read from the
 * snapshot's `project.tz`) — Trusstor passes `tz` to its row formatter
 * for the same reason: a project deployed in JP is JST, but a dev
 * project pointing at a non-Tokyo Trusstor instance carries that
 * project's own zone. Renders no time until the snapshot carries one —
 * guessing a zone would silently mis-stamp a safety alert.
 *
 * The icon's red (critical) vs orange (non-critical) colour is set by
 * the row's `color` token; the inline SVG uses `currentColor` so the
 * fill follows.
 *
 * Like Trusstor's row, hovering a row whose text is cut off shows "type,
 * floor, profession" in full, and a click opens the alert's detail dialog
 * (where End Event lives) — rendered by the list, via `useSafetyAlertDialog`.
 */
export function SafetyAlertRow({ row, onOpen }: ISafetyAlertRowProps) {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLButtonElement>(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const project = useAppSelector(selectProject);
  const tz = project?.tz;
  // Null, not a formatter, while the snapshot carries no zone. `timeZone:
  // undefined` does not mean "no zone" — it means the VIEWER's zone, so the
  // row would have stamped a safety alert in the reader's city while this
  // file's own doc comment promised it renders nothing rather than guess.
  const timeFmt = useMemo(
    () =>
      tz
        ? new Intl.DateTimeFormat('en-GB', {
            timeZone: tz,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        : null,
    [tz],
  );

  const title = t(`monitor.safetyViolationType.${row.type}`);
  const time = timeFmt ? timeFmt.format(row.timestamp) : '';

  let chipText = '';
  if (row.chip?.kind === 'trade') {
    chipText = tradeLabel(t, row.chip.tradeId) || row.chip.tradeGroup || '';
  } else if (row.chip?.kind === 'manager') {
    chipText = row.chip.name;
  }

  // The no-floor placeholder: fine in the row, noise in a sentence.
  const hoverText = [title, row.floorLabel === NO_FLOOR_LABEL ? '' : row.floorLabel, chipText]
    .filter(Boolean)
    .join(', ');

  return (
    <Tooltip
      content={hoverText}
      variant="marker"
      open={hoverOpen}
      onOpenChange={(next) => {
        // Only when something in the row is cut off — checked at hover time,
        // so it stays right as the rail resizes. The ref is set: hover needs
        // the mounted button.
        if (next && !isClipped(rowRef.current!)) return;
        setHoverOpen(next);
      }}
    >
      <button
        ref={rowRef}
        type="button"
        className={styles.safetyRow}
        data-critical={row.isCritical || undefined}
        onClick={() => onOpen(row.id)}
      >
        <span className={styles.safetyRowTop}>
          <span className={styles.safetyRowIcon} aria-hidden="true">
            <AlertCircleFilledIcon />
          </span>
          <span className={styles.safetyRowTitle}>{title}</span>
        </span>
        <span className={styles.safetyRowBottom}>
          <span className={styles.safetyRowMeta}>{time}</span>
          <span className={styles.safetyRowMeta}>{row.floorLabel}</span>
          {row.chip ? (
            <span
              className={styles.safetyRowChip}
              style={professionChipStyle(row.chip.color)}
            >
              {chipText}
            </span>
          ) : null}
        </span>
      </button>
    </Tooltip>
  );
}

/** True when any text in the row is truncated (ellipsis). */
function isClipped(row: HTMLElement): boolean {
  return [...row.querySelectorAll('span')].some((element) => element.scrollWidth > element.clientWidth);
}
